import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { FileText, DollarSign, Users, Activity, RefreshCw } from "lucide-react";
import { VentureCard, VentureCardHeader, VentureCardTitle, VentureCardContent } from "@/components/ui/venture-card";
import { BadgeVenture } from "@/components/ui/badge-venture";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { PageTour } from "@/components/tour/PageTour";
import { adminReportsSteps } from "@/lib/tour-steps";

interface BillingRow {
  id: string;
  billing_week_start: string;
  demos_delivered: number | null;
  total_amount_cents: number | null;
  fanbasis_status: string | null;
  clients: { company_name: string } | null;
}

interface SyncLogRow {
  id: string;
  integration_id: string;
  status: string | null;
  records_imported: number | null;
  created_at: string;
}

export default function AdminReports() {
  const [loading, setLoading] = useState(true);
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [totalDemos, setTotalDemos] = useState(0);
  const [activeClients, setActiveClients] = useState(0);
  const [syncHealth, setSyncHealth] = useState("0/10");
  const [recentBilling, setRecentBilling] = useState<BillingRow[]>([]);
  const [recentSyncs, setRecentSyncs] = useState<SyncLogRow[]>([]);
  const [bySource, setBySource] = useState<Record<string, number>>({});
  const [testCharges, setTestCharges] = useState(0);
  const [breakdownError, setBreakdownError] = useState(false);

  const fetchReportData = async () => {
    setLoading(true);
    try {
      // Fetch all stats in parallel
      const [paymentsRes, testChargesRes, demosRes, clientsRes, syncsRes, recentBillingRes, recentSyncsRes] = await Promise.all([
        // Real revenue (non-test). Restrict to the 6 known sources so a stray
        // `unknown` row (e.g., from historical backfill of subscription.created
        // events that predate the payment.succeeded-is-sole-writer invariant)
        // cannot inflate Total Revenue beyond Σ(breakdown card rows).
        supabase
          .from('fanbasis_payments')
          .select('amount_cents, source')
          .eq('is_test', false)
          .in('source', [
            'weekly_billing',
            'setup_fee',
            'rev_share',
            'subscription_renewal',
            'one_time_purchase',
            'refund',
          ]),
        // Test charges (separate aggregation)
        supabase
          .from('fanbasis_payments')
          .select('amount_cents')
          .eq('is_test', true),
        supabase
          .from('demos')
          .select('id', { count: 'exact', head: true })
          .eq('status', 'completed'),
        supabase
          .from('clients')
          .select('id', { count: 'exact', head: true })
          .eq('status', 'active'),
        supabase
          .from('integration_sync_logs')
          .select('status')
          .order('created_at', { ascending: false })
          .limit(10),
        supabase
          .from('weekly_billing')
          .select('id, billing_week_start, demos_delivered, total_amount_cents, fanbasis_status, clients(company_name)')
          .order('billing_week_start', { ascending: false })
          .limit(10),
        supabase
          .from('integration_sync_logs')
          .select('id, integration_id, status, records_imported, created_at')
          .order('created_at', { ascending: false })
          .limit(10),
      ]);

      // Total revenue + source breakdown (non-test)
      if (paymentsRes.error) {
        setBreakdownError(true);
        toast.error('Failed to load revenue breakdown');
        setTotalRevenue(0);
        setBySource({});
      } else if (paymentsRes.data) {
        const rows = paymentsRes.data as Array<{ amount_cents: number | null; source: string }>;
        const total = rows.reduce((sum, r) => sum + (r.amount_cents || 0), 0);
        setTotalRevenue(total);
        const grouped = rows.reduce((acc, r) => {
          const key = r.source || 'unknown';
          acc[key] = (acc[key] ?? 0) + (r.amount_cents || 0);
          return acc;
        }, {} as Record<string, number>);
        setBySource(grouped);
        setBreakdownError(false);
      }

      // Test charges (separate, muted sub-row)
      if (testChargesRes.data) {
        const testRows = testChargesRes.data as Array<{ amount_cents: number | null }>;
        setTestCharges(testRows.reduce((sum, r) => sum + (r.amount_cents || 0), 0));
      }

      // Total demos
      setTotalDemos(demosRes.count || 0);

      // Active clients
      setActiveClients(clientsRes.count || 0);

      // Sync health
      if (syncsRes.data) {
        const successful = syncsRes.data.filter(s => s.status === 'success' || s.status === 'completed').length;
        setSyncHealth(`${successful}/${syncsRes.data.length}`);
      }

      // Recent billing
      if (recentBillingRes.data) {
        setRecentBilling(recentBillingRes.data as unknown as BillingRow[]);
      }

      // Recent syncs
      if (recentSyncsRes.data) {
        setRecentSyncs(recentSyncsRes.data as SyncLogRow[]);
      }
    } catch (err) {
      console.error('Failed to fetch report data:', err);
      toast.error('Failed to load report data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReportData();
  }, []);

  const getBillingStatusBadge = (status: string | null) => {
    switch (status) {
      case 'succeeded':
        return <BadgeVenture variant="success">Succeeded</BadgeVenture>;
      case 'failed':
        return <BadgeVenture variant="error">Failed</BadgeVenture>;
      case 'pending':
        return <BadgeVenture variant="warning">Pending</BadgeVenture>;
      case 'no_charge':
        return <BadgeVenture variant="default">No Charge</BadgeVenture>;
      default:
        return <BadgeVenture variant="default">{status || 'Unknown'}</BadgeVenture>;
    }
  };

  const getSyncStatusBadge = (status: string | null) => {
    if (status === 'success' || status === 'completed') {
      return <BadgeVenture variant="success">Success</BadgeVenture>;
    }
    if (status === 'failed' || status === 'error') {
      return <BadgeVenture variant="error">Failed</BadgeVenture>;
    }
    return <BadgeVenture variant="warning">{status || 'Unknown'}</BadgeVenture>;
  };

  const formatCents = (cents: number) => {
    return `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  return (
    <motion.div
      className="space-y-6"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <PageTour steps={adminReportsSteps} />
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold">Reports</h1>
          <p className="text-muted-foreground">Billing, demos, and sync activity overview</p>
        </div>
        <Button variant="ghost" size="sm" onClick={fetchReportData} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-16">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading reports...</p>
        </div>
      ) : (
        <>
          {/* Summary Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <VentureCard>
              <VentureCardContent className="flex items-center gap-4">
                <div className="p-3 rounded-lg bg-green-500/20">
                  <DollarSign className="w-6 h-6 text-green-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{formatCents(totalRevenue)}</p>
                  <p className="text-sm text-muted-foreground">Total Revenue</p>
                </div>
              </VentureCardContent>
            </VentureCard>

            <VentureCard>
              <VentureCardContent className="flex items-center gap-4">
                <div className="p-3 rounded-lg bg-blue-500/20">
                  <FileText className="w-6 h-6 text-blue-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{totalDemos}</p>
                  <p className="text-sm text-muted-foreground">Total Demos</p>
                </div>
              </VentureCardContent>
            </VentureCard>

            <VentureCard>
              <VentureCardContent className="flex items-center gap-4">
                <div className="p-3 rounded-lg bg-purple-500/20">
                  <Users className="w-6 h-6 text-purple-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{activeClients}</p>
                  <p className="text-sm text-muted-foreground">Active Clients</p>
                </div>
              </VentureCardContent>
            </VentureCard>

            <VentureCard>
              <VentureCardContent className="flex items-center gap-4">
                <div className="p-3 rounded-lg bg-amber-500/20">
                  <Activity className="w-6 h-6 text-amber-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{syncHealth}</p>
                  <p className="text-sm text-muted-foreground">Sync Health</p>
                </div>
              </VentureCardContent>
            </VentureCard>
          </div>

          {/* Revenue Breakdown (Phase 16.9) */}
          <VentureCard>
            <VentureCardHeader>
              <VentureCardTitle>Revenue Breakdown</VentureCardTitle>
            </VentureCardHeader>
            <VentureCardContent>
              {breakdownError ? (
                <div className="text-sm text-muted-foreground text-center py-8">
                  Could not load breakdown. Click Refresh to retry.
                </div>
              ) : (
                <div>
                  {(
                    [
                      ['weekly_billing', 'Weekly demos'],
                      ['setup_fee', 'Setup fees'],
                      ['rev_share', 'Rev share'],
                      ['subscription_renewal', 'Renewals'],
                      ['one_time_purchase', 'One-time'],
                    ] as Array<[string, string]>
                  ).map(([key, label]) => {
                    const cents = bySource[key] ?? 0;
                    const negative = cents < 0;
                    const display = (negative ? '-' : '') + formatCents(Math.abs(cents));
                    return (
                      <div key={key} className="flex items-center justify-between py-2">
                        <span className="text-sm font-medium">{label}</span>
                        <span
                          className={`text-sm font-semibold tabular-nums ${negative ? 'text-destructive' : ''}`}
                          aria-label={negative ? 'refund-adjusted' : undefined}
                        >
                          {display}
                        </span>
                      </div>
                    );
                  })}
                  <div className="border-t border-border pt-3 mt-3 flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">Test charges</span>
                    <span className="text-xs text-muted-foreground tabular-nums">{formatCents(testCharges)}</span>
                  </div>
                </div>
              )}
            </VentureCardContent>
          </VentureCard>

          {/* Recent Billing */}
          <VentureCard>
            <VentureCardHeader>
              <VentureCardTitle>Recent Billing</VentureCardTitle>
            </VentureCardHeader>
            <VentureCardContent>
              {recentBilling.length === 0 ? (
                <div className="text-center py-8">
                  <DollarSign className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">No billing records yet</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[600px]">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Client</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Week</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Demos</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Amount</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentBilling.map((row) => (
                        <tr key={row.id} className="border-b border-border hover:bg-surface transition-colors">
                          <td className="py-3 px-4 font-medium">
                            {row.clients?.company_name || 'Unknown'}
                          </td>
                          <td className="py-3 px-4 text-muted-foreground">
                            {row.billing_week_start}
                          </td>
                          <td className="py-3 px-4">
                            {row.demos_delivered ?? 0}
                          </td>
                          <td className="py-3 px-4">
                            {formatCents(row.total_amount_cents || 0)}
                          </td>
                          <td className="py-3 px-4">
                            {getBillingStatusBadge(row.fanbasis_status)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </VentureCardContent>
          </VentureCard>

          {/* Recent Sync Activity */}
          <VentureCard>
            <VentureCardHeader>
              <VentureCardTitle>Recent Sync Activity</VentureCardTitle>
            </VentureCardHeader>
            <VentureCardContent>
              {recentSyncs.length === 0 ? (
                <div className="text-center py-8">
                  <Activity className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">No sync activity yet</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[600px]">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Integration ID</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Status</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Records</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentSyncs.map((row) => (
                        <tr key={row.id} className="border-b border-border hover:bg-surface transition-colors">
                          <td className="py-3 px-4 font-mono text-sm">
                            {row.integration_id.slice(0, 8)}...
                          </td>
                          <td className="py-3 px-4">
                            {getSyncStatusBadge(row.status)}
                          </td>
                          <td className="py-3 px-4">
                            {row.records_imported ?? 0}
                          </td>
                          <td className="py-3 px-4 text-muted-foreground text-sm">
                            {new Date(row.created_at).toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </VentureCardContent>
          </VentureCard>
        </>
      )}
    </motion.div>
  );
}
